'use client'

import * as stylex from '@stylexjs/stylex'
import Link from 'next/link'
import {
  Dispatch,
  MouseEvent,
  RefObject,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Button } from '../Buttons'
import { Action, ActionButtons } from '../Buttons/ActionButtons'
import { color, borderRadius, spacing } from '../../styles/tokens.stylex'
import { sendGTMEvent } from '@next/third-parties/google'
import { NextLinkWithEventPayload } from '../NextLinkWithEventPayload'
import { MainNavigationTokens } from './tokens.stylex'

type Context = {
  openMenu?: () => void
  closeMenu?: () => void
  menuRef?: RefObject<HTMLDialogElement | null>
  dialogState?: 'open' | 'closed' | null
  showSiteNav?: boolean
  setShowSiteNav?: Dispatch<SetStateAction<boolean>>
  headerActions?: Action[]
  setHeaderActions?: Dispatch<SetStateAction<Action[]>>
}

export const MobileMenuContext = createContext<Context>({})

interface MobileMenuProviderProps {
  children: React.ReactNode
}

interface MobileMenuDialogProps {
  styles?: stylex.StyleXStyles
  children: React.ReactNode
}
interface MobileMenuButtonProps {
  styles?: stylex.StyleXStyles
  children: React.ReactNode
}

export function MobileMenuProvider({ children }: MobileMenuProviderProps) {
  const [dialogState, setDialogState] = useState<Context['dialogState']>(null)
  const [showSiteNav, setShowSiteNav] = useState(true)
  const [headerActions, setHeaderActions] = useState<Action[]>([])
  const menuRef = useRef<HTMLDialogElement>(null)
  function openMenu() {
    menuRef.current?.showModal()
    setDialogState('open')
  }
  function closeMenu() {
    menuRef.current?.close()
  }
  useEffect(() => {
    function handleClose() {
      setDialogState('closed')
    }
    function handleResize() {
      if (window.innerWidth > 900) {
        menuRef.current?.close()
      }
    }

    const dialog = menuRef.current
    if (dialog) {
      dialog?.addEventListener('close', handleClose)
      dialog?.addEventListener('cancel', handleClose)
      window.addEventListener('resize', handleResize)

      return () => {
        window.removeEventListener('resize', handleResize)
        dialog?.removeEventListener('close', handleClose)
        dialog?.removeEventListener('cancel', handleClose)
      }
    }
  }, [dialogState])

  return (
    <MobileMenuContext.Provider
      value={{
        openMenu,
        closeMenu,
        dialogState,
        menuRef,
        showSiteNav,
        setShowSiteNav,
        headerActions,
        setHeaderActions,
      }}
    >
      <div
        {...stylex.props([
          sx.backgroundShiftWrapper,
          dialogState === 'open' && sx.backgroundShiftWrapperOpen,
        ])}
      >
        {children}
      </div>
    </MobileMenuContext.Provider>
  )
}

export function MobileMenuDialog({ children, styles }: MobileMenuDialogProps) {
  const { menuRef, dialogState } = useContext(MobileMenuContext)
  useEffect(() => {
    function onClose(e: Event) {
      sendGTMEvent({
        event: 'mobile_menu_close',
        mouseEvent: e,
      })
    }
    const dialog = menuRef?.current
    if (dialog) {
      dialog.addEventListener('close', onClose)
      return () => {
        dialog.removeEventListener('close', onClose)
      }
    }
  }, [menuRef])

  return (
    <dialog
      id="mobile-nav-dialog"
      data-lock-scroll
      {...stylex.props([
        sx.dialog,
        dialogState === 'open' && sx.dialogOpen,
        dialogState === 'closed' && sx.dialogClosed,
        styles,
      ])}
      ref={menuRef}
    >
      {children}
    </dialog>
  )
}

interface MenuConfigProps {
  showSiteNav: boolean
  children?: React.ReactNode
  actions?: Action[]
}

export function MenuConfig({
  children,
  showSiteNav,
  actions,
}: MenuConfigProps) {
  const { setShowSiteNav, setHeaderActions } = useContext(MobileMenuContext)
  useEffect(() => {
    if (setShowSiteNav) {
      setShowSiteNav(showSiteNav)
    }
    if (actions && setHeaderActions) {
      setHeaderActions(actions as Action[])
    }
  }, [actions, setHeaderActions, setShowSiteNav, showSiteNav])
  return <>{children}</>
}

interface DynamicLayoutProps {
  wrapperStyles?: stylex.StyleXStyles
  navStyles?: stylex.StyleXStyles
  desktopNavWrapperStyles?: stylex.StyleXStyles
  mobileDialogStyles?: stylex.StyleXStyles
  menuButtonStyles?: stylex.StyleXStyles
  actionButtonStyles?: stylex.StyleXStyles
  actions?: Action[]
  desktopNav?: React.ReactNode
  mobileNav?: React.ReactNode
}

export function DynamicHeaderLayout({
  wrapperStyles,
  navStyles,
  desktopNavWrapperStyles,
  mobileDialogStyles,
  menuButtonStyles,
  actionButtonStyles,
  desktopNav,
  mobileNav,
}: DynamicLayoutProps) {
  const { showSiteNav, headerActions } = useContext(MobileMenuContext)
  return (
    <div {...stylex.props(wrapperStyles)}>
      <nav {...stylex.props(navStyles)}>
        <div {...stylex.props(desktopNavWrapperStyles)}>
          {showSiteNav && desktopNav}
          {showSiteNav && headerActions && (
            <ActionButtons
              actions={headerActions}
              styles={actionButtonStyles}
              size="compact"
              args={{ componentName: 'MainNavigationHeader' }}
            />
          )}
        </div>
        {showSiteNav && (
          <MobileMenuDialog styles={mobileDialogStyles}>
            <div {...stylex.props(sx.mobileMenuWrapper)}>
              {mobileNav}
              {headerActions && (
                <ActionButtons
                  actions={headerActions}
                  styles={actionButtonStyles}
                  args={{ componentName: 'MobileMenuNavigation' }}
                />
              )}
            </div>
          </MobileMenuDialog>
        )}
        {!showSiteNav && headerActions && (
          <ActionButtons
            actions={headerActions}
            styles={actionButtonStyles}
            size="compact"
            args={{ componentName: 'LandingPageHeader' }}
          />
        )}
      </nav>
      {showSiteNav && (
        <OpenMenuButton
          styles={menuButtonStyles}
          aria-label="Open Navigation Menu"
        >
          Open Navigation Menu
        </OpenMenuButton>
      )}
    </div>
  )
}

interface DynamicFooterLayoutProps {
  styles?: stylex.StyleXStyles
  children?: React.ReactNode
}

export function DynamicFooterLayout({
  styles,
  children,
}: DynamicFooterLayoutProps) {
  const { showSiteNav } = useContext(MobileMenuContext)
  return <div {...stylex.props(styles)}>{showSiteNav && children}</div>
}

export function OpenMenuButton({ styles, children }: MobileMenuButtonProps) {
  const { openMenu, dialogState } = useContext(MobileMenuContext)
  function handleClick(e: React.MouseEvent<HTMLButtonElement>) {
    openMenu && openMenu()
    sendGTMEvent({
      event: 'mobile_menu_open',
      mouseEvent: e,
    })
  }
  return (
    <Button
      onClick={handleClick}
      iconOnly="text-align-justify"
      variant="ghost"
      theme="neutral"
      aria-controls="mobile-nav-dialog"
      aria-expanded={dialogState === 'open'}
      styles={styles}
      aria-label="Open Navigation Menu"
    >
      {children}
    </Button>
  )
}

export function CloseMenuButton({ styles, children }: MobileMenuButtonProps) {
  const { closeMenu, dialogState } = useContext(MobileMenuContext)
  function handleClick(e: React.MouseEvent<HTMLButtonElement>) {
    closeMenu && closeMenu()
  }
  return (
    <Button
      onClick={handleClick}
      iconOnly="x"
      variant="ghost"
      theme="neutral"
      aria-controls="mobile-nav-dialog"
      aria-expanded={dialogState === 'open'}
      styles={styles}
    >
      {children}
    </Button>
  )
}

type MenuLinkProps = React.ComponentProps<typeof Link> & {
  clickEventPayload?: Record<string, unknown>
}

export function MenuLink({
  clickEventPayload,
  children,
  ...props
}: MenuLinkProps) {
  const { closeMenu } = useContext(MobileMenuContext)
  function handleClick(e: MouseEvent<HTMLAnchorElement>) {
    if (closeMenu) {
      closeMenu()
    }
  }
  return (
    <NextLinkWithEventPayload
      clickEventPayload={clickEventPayload}
      onClick={handleClick}
      {...props}
    >
      {children}
    </NextLinkWithEventPayload>
  )
}

const backDropOpen = stylex.keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
})
const backDropClose = stylex.keyframes({
  from: { opacity: 1 },
  to: { opacity: 0 },
})

const dialogOpen = stylex.keyframes({
  from: { transform: 'translateX(100%)' },
  to: { transform: 'translateX(0%)' },
})
const dialogClose = stylex.keyframes({
  from: { transform: 'translateX(0%)' },
  to: { transform: 'translateX(100%)' },
})

const sx = stylex.create({
  dialog: {
    '::backdrop': {
      backgroundColor: 'rgba(0,0,0,0.3)',
      backdropFilter: 'blur(6px)',
      // eslint-disable-next-line @stylexjs/valid-styles
      WebkitBackdropFilter: 'blur(6px)',
      animationDuration: '0.3s',
      animationFillMode: 'forwards',
      willChange: 'opacity',
      opacity: '0',
    },
    transform: 'translateX(100%)',
    boxSizing: 'border-box',
    // eslint-disable-next-line @stylexjs/valid-styles
    transitionBehavior: 'allow-discrete',
    transitionProperty: 'display, overlay',
    willChange: 'display, overlay, transform',
    transitionTimingFunction: 'linear',
    transitionDuration: '0.3s',
    animationDuration: '0.3s',
    animationFillMode: 'forwards',
    borderStyle: 'none',
    borderWidth: '0',
    padding: '0',
    margin: '0',
    animationTimingFunction: 'cubic-bezier(0.4, 0.1, 0.5, 0.9)',
    minHeight: '100dvh',
    insetInlineStart: '0',
    insetBlockEnd: '0',
    insetBlockStart: '0',
    minWidth: '100%',
    maxWidth: 'unset',
    backgroundColor: color.surfaceBasePrimary,
    borderTopLeftRadius: borderRadius.surface_large,
    borderBottomLeftRadius: borderRadius.surface_large,
    marginLeft: `calc(${spacing.inset_lrg} * -1)`,
    width: `calc(100vw + ${spacing.inset_lrg})`,
    paddingInlineStart: `calc(${spacing.inset_lrg} * 2)`,
  },
  dialogOpen: {
    animationName: dialogOpen,
    '::backdrop': {
      animationName: backDropOpen,
    },
  },
  dialogClosed: {
    animationName: dialogClose,
    '::backdrop': {
      animationName: backDropClose,
    },
  },
  mobileMenuWrapper: {
    boxSizing: 'border-box',
    minHeight: '100dvh',
    display: 'grid',
    gridTemplateRows: 'auto 1fr auto',
    paddingInlineEnd: spacing.inset_lrg,
    paddingBlockEnd: spacing.inset_lrg,
  },
  backgroundShiftWrapper: {
    transitionDuration: '0.3s',
    transitionProperty: 'transform',
    minHeight: '100dvh',
    display: 'grid',
    gridTemplateRows: 'auto 1fr auto',
    // eslint-disable-next-line @stylexjs/valid-styles
    // overflowX: stylex.firstThatWorks(
    //   'clip',
    //   '-moz-hidden-unscrollable',
    //   'hidden'
    // ),
  },
  backgroundShiftWrapperOpen: {
    transform: {
      default: 'translateX(-2vw)',
      [MainNavigationTokens.REDUCED_MOTION]: 'none',
    },
  },
})
